/**
 * Zentrale Anwendungs-Konfiguration für bootstrapApplication (standalone).
 *
 * Ersetzt das frühere AppModule: Router, HttpClient (funktionale Interceptor-
 * Kette), NgRx-Root-Store, OIDC-Auth, Logging, Matomo und die App-weiten
 * Provider werden hier über die Angular-17-provide-APIs registriert.
 * Library-Module ohne provide-API laufen über importProvidersFrom().
 */
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, LOCALE_ID, importProvidersFrom } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  AuthModule,
  OpenIdConfiguration,
  StsConfigHttpLoader,
  StsConfigLoader,
  authInterceptor,
} from 'angular-auth-oidc-client';
import { LoggerModule, NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { MatomoConsentMode, MatomoInitializationMode, provideMatomo } from 'ngx-matomo-client';
import * as Sentry from '@sentry/angular-ivy';
import { take } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AppSkeletonModule,
  CONTEXT_INFO,
  ErrorPagesModule,
  INHABER_SERVICE,
  NGX_LOGGER,
  NavigationModule,
} from '@adnova/jf-ng-components';

import { environment } from '@environments/environment';
import { APP_ROUTES } from './app.routes';
import {
  ApiModule as ChatbotApiInternalModule,
  Configuration as ChatbotConfiguration,
} from './openapi/chatbot-assistant-openapi';
import { ConfigModule } from './config/config.module';
import { ConfigService } from './config/service/config.service';
import { xUserIdInterceptor } from './auth/x-user-id.interceptor';
import { ContextInfoService } from './portal/context-info.service';
import { metaReducers, reducers } from './store/reducers/reducers';
import { MockInhaberService } from './mocks/services/mock-inhaber.service';
import { AppInitializerService } from './core/services/app-initializer.service';

registerLocaleData(localeDe, 'de-DE', localeDeExtra);

/** OIDC-Konfiguration wird nach dem Config-Load aufgebaut (APP_INITIALIZER-Kette). */
function authConfigFactory(configService: ConfigService): StsConfigHttpLoader {
  const configObservable = configService.loaded$.pipe(
    take(1),
    map((config) => {
      const authConfig: OpenIdConfiguration = {
        // loaded
        authority: config.auth.authority,
        clientId: config.auth.clientId,
        scope: config.auth.scope,
        logLevel: config.auth.logLevel,
        renewTimeBeforeTokenExpiresInSeconds: config.auth.renewTimeBeforeTokenExpiresInSeconds,
        // predefined
        redirectUrl: window.location.origin,
        postLoginRoute: '/chat', // CRITICAL: Nach OAuth Login zu /chat navigieren
        // Leerer String unterdrueckt den post_logout_redirect_uri-Parameter bei der
        // Abmeldung. WICHTIG: Das Feld muss explizit auf '' stehen - laesst man es weg,
        // setzt die Library ihren Default 'https://please_set' ein (DEFAULT_CONFIG), der
        // dann gesendet und von Keycloak mit "Invalid redirect uri" abgelehnt wird.
        // Ohne den Parameter entfaellt die Redirect-Validierung bei der Abmeldung.
        // Trade-off: Der Nutzer landet nach dem Logout auf der Abmelde-Seite von Keycloak
        // statt automatisch zurueck in der App. Fuer den Auto-Ruecksprung hier
        // "window.location.origin" setzen UND die Origin im Keycloak-Client unter
        // "Valid post logout redirect URIs" pflegen.
        postLogoutRedirectUri: '',
        responseType: 'code',
        silentRenew: true,
        triggerAuthorizationResultEvent: true,
        ignoreNonceAfterRefresh: true,
        useRefreshToken: true,
        // INFO: Bei Anpassungen auch xUserIdInterceptor.needsUserIdHeader() synchronisieren.
        // Proxy-Modus (leere/relative apiBaseUrl) -> relative /api/* Route und same-origin /api/*
        secureRoutes:
          !config.chatbot.apiBaseUrl || config.chatbot.apiBaseUrl.startsWith('/')
            ? ['/api/', `${window.location.origin}/api/`]
            : [config.chatbot.apiBaseUrl],
      };
      return authConfig;
    }),
  );
  return new StsConfigHttpLoader(configObservable);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideRouter(
      APP_ROUTES,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
    ),
    // Funktionale Interceptor-Kette: X-User-ID-Workaround vor dem
    // Bearer-Attachment der OIDC-Library (secureRoutes-Konfiguration).
    provideHttpClient(withInterceptors([xUserIdInterceptor, authInterceptor()])),
    // NgRx Root Store über die Klassen-API: Die JF-Library registriert ihren
    // State intern via StoreModule/EffectsModule.forFeature - das setzt die
    // forRoot-Klassenmodule voraus (provideStore/provideEffects reichen nicht).
    importProvidersFrom(
      StoreModule.forRoot(reducers, {
        metaReducers,
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictActionSerializability: true,
          strictStateSerializability: true,
        },
      }),
      EffectsModule.forRoot([]),
      StoreRouterConnectingModule.forRoot({}),
      // DevTools nur außerhalb Production: logOnly schaltet die Instrumentierung
      // NICHT ab – der komplette NgRx-State (Chat-Inhalte) wäre sonst in Prod
      // über die Redux-DevTools-Extension einsehbar.
      ...(environment.production
        ? []
        : [StoreDevtoolsModule.instrument({ name: 'Chatbot', maxAge: 25, connectInZone: true })]),
    ),
    provideMatomo({
      mode: MatomoInitializationMode.AUTO_DEFERRED,
      requireConsent: MatomoConsentMode.TRACKING,
    }),
    // Library-Module ohne provide-API (Provider-Extraktion)
    importProvidersFrom(
      MatDialogModule,
      MatSnackBarModule,
      AppSkeletonModule,
      NavigationModule,
      ErrorPagesModule,
      ConfigModule,
      LoggerModule.forRoot({
        level: environment.production ? NgxLoggerLevel.WARN : NgxLoggerLevel.INFO,
        enableSourceMaps: !environment.production,
      }),
      AuthModule.forRoot({
        loader: {
          provide: StsConfigLoader,
          useFactory: authConfigFactory,
          deps: [ConfigService],
        },
      }),
      ChatbotApiInternalModule.forRoot(() => new ChatbotConfiguration()),
    ),
    {
      // INFO: Angular Sentry ErrorHandler
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      // INFO: Zentraler App Initializer mit phasenbasierter Initialisierung
      // Reihenfolge: CONFIG -> LOGGING -> MONITORING -> INTEGRATION -> AUTH
      provide: APP_INITIALIZER,
      useFactory: (appInitializer: AppInitializerService) => () => appInitializer.initialize(),
      deps: [AppInitializerService],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
    {
      provide: CONTEXT_INFO,
      useClass: ContextInfoService,
    },
    {
      provide: INHABER_SERVICE,
      useClass: MockInhaberService,
    },
    {
      provide: NGX_LOGGER,
      useClass: NGXLogger,
    },
  ],
};
